// Shared agent-conversation rendering toolkit — the per-turn renderer (TurnEntry)
// and its section sub-components, MOVED 1:1 out of AgentDetail (Alex 2026-06-26)
// so both the single-agent view (AgentDetail) and the multi-agent tiles
// (MultiAgent/AgentCard) render the SAME conversation core. No rewrite — the code
// is physically relocated; only `export` was added to the top-level declarations.
import { useState, useRef, memo, useSyncExternalStore } from 'react';
import Markdown from 'react-markdown';
import { segmentBySpans, segmentClass, type TextSpan } from '../lib/spans';
import { personName, subscribePeople, peopleVersion } from '../lib/people';
import { ChevronRight, Square, Volume2, Copy, Check, Send, Reply, Megaphone, Inbox, RotateCcw, RotateCw, Play, Pause, Loader2 } from 'lucide-react';
import { useTts } from '../hooks/useTts';
import { getTtsMode, type TtsMode } from '../lib/tts';
import { StorageMedia } from './StorageMedia';
import { AudioBlock } from './AudioBlock';
import { IacpEventCard } from './IacpEventCard';
import { ArcturianMessage } from './ArcturianMessage';
import { parseArcturianEnvelope } from '../lib/arcturianEnvelope';
import { turnNarrationText } from '../lib/narration';
import { tryParseJson, PrettyJson, SemanticAgentCard, hasSemanticFields, splitTextAndJson } from './agentJson';
import { autoWrapMermaid } from './agentMermaid';
import { linkifyPostRefs } from '../lib/postRefs';
import { serviceOrigin } from '../lib/services';
import {
  mdComponents,
  REMARK_PLUGINS,
  REHYPE_PLUGINS,
  stripAnsi,
  STORAGE_MEDIA,
  storageMediaUrl,
  IMAGE_EXTS,
  AUDIO_EXTS,
} from './richMarkdown';
import type { V2Turn, V2Section, IacpEvent } from '../api/types';
import { useInstanceMode } from '../hooks/useInstanceMode';
import './AgentDetail.css';

export function sourceLabel(source: string): string | null {
  if (source === 'queue') return 'Queue';
  if (source === 'iacp') return 'IACP';
  return null;
}

// Lightweight format sniffer for IACP / Queue payloads — tells the
// TurnEntry render whether to offer a "pretty" toggle. JSON wins by
// successful parse; XML wins by leading "<" and at least a couple of
// balanced angle-bracket pairs (heuristic, no DOM parse). Plain text
// is the fallback and shows no toggle. Alex's GPS-update queue
// message (XML wrapped in narrative prose) hit screenshot 110031.
// Bounded fallback for a raw blob that couldn't be parsed into a structured
// section (e.g. a Codex tool-result whose JSON got split across chunks — post
// #4231). Small blobs render inline; large ones collapse behind a disclosure
// with a scroll-capped body, so a malformed/huge array can NEVER spill unbounded
// into the transcript (ArkturianCodex gold-rule, 2026-07-14). Belt-and-suspenders
// at the render layer — independent of the /v2 codex parser fix (Cloud's lane).
/**
 * Zuschreibung als NAME, Adresse nur als Beleg im Tooltip (Alex, 19.08.):
 * „Ich möchte nicht, dass da meine E-Mail steht, sondern mein Name." Die
 * Zuschreibung selbst bleibt — sie ist der Nachweis, wer gesendet hat.
 * Ist kein Name bekannt, steht die Kennung unverändert da; ein geratener Name
 * über einer fremden Nachricht wäre schlimmer als eine technische Adresse.
 */
function SubmitterName({ id }: { id: string }) {
  useSyncExternalStore(subscribePeople, peopleVersion, peopleVersion);
  return <span title={`Authentifizierter Absender: ${id}`}>{personName(id)}</span>;
}

/**
 * Vorformatierter Block mit der Terminal-Auszeichnung. Genau hier lebt Codex'
 * Farbe: Dateinamen, Diffs, Fehlerzeilen.
 *
 * Zwei Schutzmaßnahmen, beide bewusst konservativ:
 * • Offsets zählen auf das AUSGELIEFERTE `content`. Würde ich vorher etwas
 *   entfernen, verschöben sie sich — deshalb wird `stripAnsi` nur angewandt,
 *   wenn es nichts ändert; andernfalls verzichte ich auf die Auszeichnung
 *   statt sie falsch zu setzen.
 * • Ohne Bereiche exakt das alte Verhalten (ein <pre>, bereinigter Text).
 */
function ColoredPre({ content, spans, className }: { content: string; spans?: TextSpan[]; className?: string }) {
  const cleaned = stripAnsi(content);
  const usable = spans && spans.length > 0 && cleaned === content;
  if (!usable) return <pre className={className || 'code-block'}>{cleaned}</pre>;
  return (
    <pre className={className || 'code-block'}>
      {segmentBySpans(content, spans).map((seg, i) => {
        const cls = segmentClass(seg);
        return cls ? <span key={i} className={cls}>{seg.text}</span> : <span key={i}>{seg.text}</span>;
      })}
    </pre>
  );
}

function RawFallback({ content }: { content: string }) {
  const c = content || '';
  const big = c.length > 1200 || c.split('\n').length > 14;
  if (!big) return <pre className="code-block">{c}</pre>;
  return (
    <details className="raw-fallback">
      <summary className="raw-fallback-sum">Rohdaten ({c.length.toLocaleString('de-DE')} Zeichen) — anzeigen</summary>
      <pre className="code-block raw-fallback-pre">{c}</pre>
    </details>
  );
}

// Extract the JSON result payload from a tool section's content. The section
// often carries the "Called X(...)" / "Calling X(...)" header line and a tree
// marker (└ / ⎿) BEFORE the actual JSON — so tryParseJson on the raw content
// fails despite a complete section (ArkturianCodex #4231). Strip those EXPLICIT
// markers (no indentation guessing) and parse from the first JSON opener.
function extractToolResultJson(raw: string): unknown | null {
  if (!raw) return null;
  // The /v2 tool body carries the call-header BEFORE the JSON, e.g.
  //   **Called** cloud.list_agents({})\n└ {"agents": [...]}
  // — with markdown-bold artifacts (`**` from ANSI→md conversion) and a `└`/`⎿`
  // tree marker (Cloud's turn-14 dissection, #4231). Strip those EXPLICIT markers
  // (optional `**`/`•` too — no indentation guessing), then parse from the first
  // JSON opener. Works on today's mixed body AND on Cloud's future clean split.
  const s = raw
    .replace(/^\s*[•●]?\s*(?:\*\*)?\s*(?:Called|Calling)\b[^\n]*\n?/i, '') // drop call-header line
    .replace(/[└⎿]\s?/g, '')                                               // strip tree markers
    .replace(/^\s+/, '');
  // Must BE a JSON result, not merely CONTAIN a `{` — otherwise a shell command
  // body like `Ran printf '{"from":…}' | curl …` (embedded JSON argument, #4231
  // 113620) or JSON-in-prose would get falsely structured. Only parse when the
  // stripped body STARTS with an opener and parses whole.
  if (s[0] !== '{' && s[0] !== '[') return null;
  return tryParseJson(s);
}

export type PayloadFormat = 'json' | 'xml' | 'text';
export function detectFormat(raw: string): PayloadFormat {
  const t = raw.trim();
  if (!t) return 'text';
  if (t[0] === '{' || t[0] === '[') {
    try { JSON.parse(t); return 'json'; } catch { /* fall through */ }
  }
  if (t[0] === '<') {
    const opens = (t.match(/<[a-zA-Z!?]/g) || []).length;
    const closes = (t.match(/>/g) || []).length;
    if (opens >= 2 && closes >= 2) return 'xml';
  }
  return 'text';
}

// Pretty-printers. JSON via JSON.stringify, XML via a small hand-rolled
// formatter that puts each tag on its own line with indent — no DOM
// parsing so it survives the occasional malformed payload Claude emits.
export function prettyFormat(raw: string, fmt: PayloadFormat): string {
  if (fmt === 'json') {
    try { return JSON.stringify(JSON.parse(raw.trim()), null, 2); }
    catch { return raw; }
  }
  if (fmt === 'xml') {
    let depth = 0;
    const lines: string[] = [];
    // Split on tags while keeping the tags themselves.
    const tokens = raw.trim().split(/(<[^>]+>)/).filter(Boolean);
    for (const tok of tokens) {
      const trimmed = tok.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith('</')) {
        depth = Math.max(0, depth - 1);
        lines.push('  '.repeat(depth) + trimmed);
      } else if (trimmed.startsWith('<') && trimmed.endsWith('/>')) {
        lines.push('  '.repeat(depth) + trimmed);          // self-closing
      } else if (trimmed.startsWith('<')) {
        lines.push('  '.repeat(depth) + trimmed);
        depth += 1;
      } else {
        lines.push('  '.repeat(depth) + trimmed);          // text content
      }
    }
    return lines.join('\n');
  }
  return raw;
}

export function parseTurnMeta(turn: V2Turn): { sender: string | null; cleanText: string } {
  // Use API sender field if available
  if (turn.sender) {
    return { sender: turn.sender, cleanText: cleanIACPText(turn.user) };
  }

  // Fallback: parse [IACP:id:SenderName] prefix from text
  const iacpMatch = turn.user.match(/^\[IACP:([^\]]*):([^\]]+)\]\s*/);
  if (iacpMatch) {
    return { sender: iacpMatch[2], cleanText: cleanIACPText(turn.user.slice(iacpMatch[0].length)) };
  }

  // Fallback: parse [Queue Message #N from SenderName]: prefix
  const queueMatch = turn.user.match(/^\[Queue Message #\d+ from ([^\]]+)\]:\s*/);
  if (queueMatch) {
    return { sender: queueMatch[1], cleanText: cleanIACPText(turn.user.slice(queueMatch[0].length)) };
  }

  return { sender: null, cleanText: cleanIACPText(turn.user) };
}

export function cleanIACPText(text: string): string {
  // Strip [IACP-PROTOCOL: ...] suffix
  return text.replace(/\s*\[IACP-PROTOCOL:[^\]]*\]\s*$/, '').trim();
}

// Escape control chars (newlines / tabs / carriage returns) that appear
// raw INSIDE string values. LLM-emitted JSON routinely violates RFC 8259
// by including literal 0x0A bytes in string contents (e.g. multi-line
// `content` fields written without escape). Strict JSON.parse rejects
// these — we walk the candidate, track string state, and rewrite raw
// control chars to their JSON-escape form before re-parsing. Braces
// inside strings stay intact because we respect the string boundaries.

export function TaskListSection({ section }: { section: V2Section }) {
  const [open, setOpen] = useState(false);
  const items = section.items ?? [];
  // summary is a union (object for tasklist, string for recap) — narrow.
  const summary = (typeof section.summary === 'object' && section.summary !== null)
    ? section.summary : null;
  const extraPending = section.extra_pending ?? 0;
  const extraCompleted = section.extra_completed ?? 0;

  // Compose a compact label. Prefer header-summary numbers when present
  // (they cover the truncated done-pile too); otherwise count the items
  // we actually parsed. In_progress + pending are both "still open".
  const inProgressCount = items.filter((i) => i.status === 'in_progress' || i.in_progress).length;
  const openItemsCount = summary?.open ?? items.filter((i) => !i.done).length;
  // include extra pending mentioned in the truncation footer
  const totalOpen = openItemsCount + (summary ? 0 : extraPending);
  const label = totalOpen > 0
    ? `Tasks · ${totalOpen} open${inProgressCount > 0 ? ` · ${inProgressCount} running` : ''}`
    : 'Tasks';

  // Footer summary line — what's NOT shown in the body
  const doneFromItems = items.filter((i) => i.done).length;
  const totalDone = summary?.done ?? (doneFromItems + extraCompleted);
  const showFooter = extraPending > 0 || extraCompleted > 0 || totalDone > doneFromItems;

  return (
    <div className={`v2-tasklist ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="v2-tasklist-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <ChevronRight
          size={14}
          className={`v2-tasklist-chevron ${open ? 'is-open' : ''}`}
        />
        <span className="v2-tasklist-label">{label}</span>
      </button>
      {open && (
        <div className="v2-tasklist-body">
          {section.markdown && (
            <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
              {linkifyPostRefs(autoWrapMermaid(section.markdown), serviceOrigin('content_web'))}
            </Markdown>
          )}
          {showFooter && (
            <div className="v2-tasklist-footer">
              {extraPending > 0 && <span>+{extraPending} pending</span>}
              {extraPending > 0 && (extraCompleted > 0 || totalDone > 0) && <span> · </span>}
              {(extraCompleted > 0 || totalDone > 0) && <span>{totalDone || extraCompleted} completed</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Split a free-text "Next:" suggestion into individual options. The
// backend gives `next` as prose (Cloud IACP d3e3c1fa: "no structured
// array"), commonly a comma/"or"-separated list — e.g. "spinning-wheel
// animation, a different bike style, or new colors". Best-effort split on
// commas + trailing "or"; if it doesn't look like a list (one long
// sentence, no commas) we render it as a single block instead of chips.
export function splitNextOptions(next: string): string[] {
  const t = next.trim().replace(/\.$/, '');
  if (!t.includes(',') && !/\bor\b/i.test(t)) return [];
  const parts = t
    .split(/,\s*(?:or\s+)?|\s+or\s+/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 1 && s.length < 80);
  // Need at least 2 plausible options to be worth chip-ifying.
  return parts.length >= 2 ? parts : [];
}

export function RecapCard({ section }: { section: V2Section }) {
  const summary = typeof section.summary === 'string' ? section.summary : (section.content || '');
  const next = typeof section.next === 'string' ? section.next.trim() : '';
  const options = next ? splitNextOptions(next) : [];

  // Prefill the input box with a follow-up when a Next-chip is clicked.
  // Decoupled via a window event so we don't thread a callback through
  // the whole turn-render tree; InputBar (active session) listens.
  const prefill = (text: string) => {
    window.dispatchEvent(new CustomEvent('cloud-prefill-input', { detail: { text } }));
  };

  return (
    <div className="entry-content">
      <div className="recap-card">
        <div className="recap-head"><span className="recap-icon">✨</span> Recap</div>
        {summary && <div className="recap-summary">{summary}</div>}
        {next && (
          <div className="recap-next">
            <span className="recap-next-label">Next:</span>
            {options.length > 0 ? (
              <span className="recap-chips">
                {options.map((o, i) => (
                  <button key={i} type="button" className="recap-chip" onClick={() => prefill(o)} title="In die Eingabe übernehmen">
                    {o}
                  </button>
                ))}
              </span>
            ) : (
              <span className="recap-next-text">{next}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionContent({ section }: { section: V2Section }) {
  if (section.kind === 'text') {
    const content = stripAnsi(section.content || '');
    // Markdown-freie Prosa MIT Auszeichnung: als segmentierter Klartext, damit
    // Codex' Farbe auch außerhalb der Werkzeug-Blöcke ankommt. Nur wenn der
    // Dienst `plain` gesetzt hat (er prüft streng) UND meine Bereinigung nichts
    // verändert hat — sonst stimmen die Offsets nicht mehr, und dann lieber
    // ungefärbt als falsch gefärbt.
    if (section.plain && section.spans?.length && content === section.content) {
      return (
        <div className="entry-content">
          <div className="entry-plain">
            {segmentBySpans(section.content, section.spans).map((seg, i) => {
              const cls = segmentClass(seg);
              return cls ? <span key={i} className={cls}>{seg.text}</span> : <span key={i}>{seg.text}</span>;
            })}
          </div>
        </div>
      );
    }
    const parts = splitTextAndJson(content);
    const hasJson = parts.some((p) => p.type === 'json');
    if (hasJson) {
      return (
        <div className="entry-content">
          {parts.map((p, i) =>
            p.type === 'json' && p.data ? (
              <PrettyJson key={i} data={p.data} />
            ) : (
              <Markdown key={i} remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
                {linkifyPostRefs(autoWrapMermaid(p.content), serviceOrigin('content_web'))}
              </Markdown>
            )
          )}
        </div>
      );
    }
    return (
      <div className="entry-content">
        <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
          {linkifyPostRefs(autoWrapMermaid(content), serviceOrigin('content_web'))}
        </Markdown>
      </div>
    );
  }

  if (section.kind === 'json') {
    const jsonData = tryParseJson(section.content || '');
    if (jsonData) {
      // Semantic agent-card rendering for persona-JSON output (Cloud
      // IACP 57850f3a). Bots with native_json_format=true or pool-persona
      // schemas emit recognised top-level fields (tts_summary, ergebnis,
      // empfehlungen, memory_notes, _apus_chunk_) which render as
      // labelled sub-blocks instead of a raw key-value dump. Unknown
      // schemas fall through to PrettyJson — back-compat safe.
      if (hasSemanticFields(jsonData)) {
        return <div className="entry-content"><SemanticAgentCard data={jsonData as Record<string, unknown>} /></div>;
      }
      return <div className="entry-content"><PrettyJson data={jsonData} /></div>;
    }
    // Unparseable (split/malformed JSON, e.g. a chunk-boundary tool result) →
    // bounded fallback, never an unbounded raw wall.
    return <div className="entry-content"><RawFallback content={section.content || ''} /></div>;
  }

  if (section.kind === 'table' && section.markdown) {
    // A table with a single data row is a record, not a comparison — as a grid
    // it spends the full width on one value per column and forces the eye to
    // travel between header and cell (Alex 2026-08-03: "dann soll sie nicht wie
    // ein Gitter dargestellt werden, sondern die Zeile untereinander").
    // Stacked label/value reads in one glance. Two or more rows stay a table,
    // because THEN the columns are what carries the meaning.
    const single = parseSingleRowTable(section.markdown);
    if (single) return <SingleRowTable pairs={single} />;
    return (
      <div className="v2-table">
        <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
          {section.markdown}
        </Markdown>
      </div>
    );
  }

  if (section.kind === 'tasklist' && section.markdown) {
    return <TaskListSection section={section} />;
  }

  if (section.kind === 'recap') {
    return <RecapCard section={section} />;
  }

  if (section.kind === 'tree') {
    const lines: string[] = section.raw_lines || [];
    if (lines.length === 0) {
      return (
        <div className="entry-content">
          <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
            {section.markdown_fallback || ''}
          </Markdown>
        </div>
      );
    }
    return (
      <div className="v2-tree">
        {lines.map((line, i) => {
          const m = line.match(/^([\s│├└─]*)(.*)$/);
          const prefix = m ? m[1] : '';
          const rest = m ? m[2] : line;
          const annMatch = rest.match(/^(.*?)(\s{2,})(.+)$/);
          const name = annMatch ? annMatch[1] : rest;
          const annotation = annMatch ? annMatch[3] : '';
          const isFolder = name.trimEnd().endsWith('/');
          return (
            <div key={i} className="v2-tree-row">
              <span className="v2-tree-prefix">{prefix}</span>
              <span className={`v2-tree-name${isFolder ? ' folder' : ''}`}>{name}</span>
              {annotation && <span className="v2-tree-annotation">{annotation}</span>}
            </div>
          );
        })}
      </div>
    );
  }

  if (section.kind === 'media') {
    if (section.media_type === 'image') {
      return <img src={section.url} alt="" style={{ maxWidth: '100%', marginTop: 8 }} />;
    }
    if (section.media_type === 'audio' && section.url) {
      return <AudioBlock src={section.url} style={{ marginTop: 8 }} />;
    }
    if (section.media_type === 'video' && section.url) {
      if (STORAGE_MEDIA.test(section.url)) {
        return <div style={{ marginTop: 8 }}><StorageMedia url={section.url} /></div>;
      }
      return <video controls src={section.url} style={{ maxWidth: '100%', marginTop: 8 }} playsInline />;
    }
  }

  return null;
}

// A clickable federation target: jumps straight to that agent's conversation
// (Alex 2026-08-02: "dann möchte ich da draufklicken können, dann komme ich
// direkt auf die Konversationsseite"). Routed through a window event rather
// than a prop because ToolSection renders inside both AgentDetail and the
// MultiAgent grid — threading a callback through every turn/section would touch
// far more code than the feature is worth.
function IacpTargetLink({ name }: { name: string }) {
  return (
    <button
      type="button"
      className="iacp-target iacp-target-link"
      title={`${name} öffnen`}
      onClick={(e) => {
        // The header row itself toggles expand/collapse — without this, opening
        // the agent would also expand the tool body behind it.
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('cloud-open-agent', { detail: { name } }));
      }}
    >
      {name}
    </button>
  );
}

export function IacpToolHeader({ iacp }: { iacp: NonNullable<V2Section['iacp']> }) {
  // Compact, recognisable headers per IACP verb. Icons + accent colour make
  // federation traffic stand out from local Bash/Read/Edit calls without
  // forcing the user to expand each row to find out who was contacted.
  switch (iacp.tool) {
    case 'send_message':
      return (
        <>
          <Send size={12} className="iacp-icon" />
          <span className="iacp-label">an</span>
          {iacp.target
            ? <IacpTargetLink name={iacp.target} />
            : <span className="iacp-target iacp-target-unknown">?</span>}
        </>
      );
    case 'reply':
      return (
        <>
          <Reply size={12} className="iacp-icon" />
          <span className="iacp-label">Antwort</span>
          <span className="iacp-target">{iacp.msg_id ? `msg ${iacp.msg_id.slice(0, 8)}` : '?'}</span>
        </>
      );
    case 'send_to_role':
      return (
        <>
          <Megaphone size={12} className="iacp-icon" />
          <span className="iacp-label">Broadcast</span>
          <span className="iacp-target">{iacp.target || '?'}</span>
        </>
      );
    case 'inbox':
      return (
        <>
          <Inbox size={12} className="iacp-icon" />
          <span className="iacp-label">Inbox</span>
          {/* send_to_role stays plain below: its target is a ROLE, not an agent —
              clicking it could not open a conversation. */}
          {iacp.target ? <IacpTargetLink name={iacp.target} /> : null}
        </>
      );
  }
}

// Pre-backend-patch fallback: backend currently emits exactly
// "Called cloud (ctrl+o to expand)" for IACP traffic without args. Promote
// these from the generic-tool look to a federation tag so it's at least
// obvious that something cross-agent happened — even if we still can't
// say WHO. Backend will replace this with section.iacp once Cloud lands
// her v2_parser patch.
// Both tenses: the CLI writes "Called cloud (ctrl+o to expand)" once the call
// has returned, but "Calling cloud… (ctrl+o to expand)" while it is still in
// flight (Alex's screenshot 117234). Only the past tense was matched, so a
// running federation call rendered as an anonymous grey tool row.
// Split one markdown table row into cells. Escaped pipes (`\|`) are cell
// CONTENT, not separators — splitting on them would shred a cell that legally
// contains a pipe.
function splitRow(line: string): string[] {
  // Split on pipes that are NOT escaped. Doing it with a sentinel character
  // instead was the first attempt and was wrong: substituting the sentinel back
  // hit every occurrence, so a cell containing an ordinary space came out with
  // a pipe in it.
  return line
    .split(/(?<!\\)\|/)
    .map((c) => c.replace(/\\\|/g, '|').trim())
    .filter((c, i, a) => !((i === 0 || i === a.length - 1) && c === ''));
}

/**
 * Returns [header, value] pairs when `markdown` is a table with EXACTLY one data
 * row — otherwise null, and the caller renders a normal table. Strict on
 * purpose: a mismatched column count means the parse is wrong, and rendering a
 * wrong record is worse than rendering a wide table.
 */
function parseSingleRowTable(markdown: string): Array<[string, string]> | null {
  const lines = markdown.split('\n').map((l) => l.trim()).filter(Boolean);
  const sepIdx = lines.findIndex((l) => /^\|?\s*:?-{2,}/.test(l) && l.includes('-'));
  if (sepIdx !== 1) return null;               // header must be the line above it
  const rows = lines.slice(sepIdx + 1).filter((l) => l.includes('|'));
  if (rows.length !== 1) return null;          // 0 rows = empty, 2+ = real table
  const header = splitRow(lines[0]);
  const values = splitRow(rows[0]);
  if (header.length < 2 || header.length !== values.length) return null;
  // All-empty row (a table skeleton the agent has not filled yet) — keep the
  // grid so it stays visible as an empty table rather than a blank block.
  if (values.every((v) => !v)) return null;
  return header.map((h, i) => [h, values[i]] as [string, string]);
}

function SingleRowTable({ pairs }: { pairs: Array<[string, string]> }) {
  return (
    <div className="v2-record">
      {pairs.map(([label, value], i) => (
        <div className="v2-record-row" key={i}>
          <div className="v2-record-label">{label}</div>
          <div className="v2-record-value">
            <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
              {value}
            </Markdown>
          </div>
        </div>
      ))}
    </div>
  );
}

export const IACP_FALLBACK_LABEL = /^Call(?:ed|ing)\s+cloud\b/i;

// An incoming push whose body is the raw notification payload rather than prose.
// Only accepts what IacpEventCard actually needs (`event` + `post_id`); anything
// else falls through to normal text rendering, because a half-parsed card would
// hide content instead of structuring it. Deliberately tolerant about the
// `[IACP:id:sender]` envelope, which is present in some panes and stripped in
// others.
export function parseEmbeddedIacpEvent(text: string | undefined | null): IacpEvent | null {
  if (!text) return null;
  const body = text.replace(/^\s*\[IACP:[^\]]*\]\s*/i, '').trim();
  if (!body.startsWith('{') || !body.includes('"event"')) return null;
  // The pane may append trailing notes after the JSON (e.g. the IACP-PROTOCOL
  // footer), so parse the balanced object rather than the whole string.
  let depth = 0;
  let end = -1;
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '{') depth++;
    else if (body[i] === '}' && --depth === 0) { end = i + 1; break; }
  }
  if (end < 0) return salvageTruncatedIacpEvent(body);
  try {
    const o = JSON.parse(body.slice(0, end)) as Partial<IacpEvent>;
    if (typeof o.event !== 'string' || typeof o.post_id !== 'number') return null;
    return o as IacpEvent;
  } catch {
    return salvageTruncatedIacpEvent(body);
  }
}

/**
 * Rettet einen ABGESCHNITTENEN chunk_committed-Payload.
 *
 * Der Zustellweg kappt große Nachrichten mitten im JSON und hängt
 * „[FULL MESSAGE in inbox — …]" an. Damit gibt es keine schließende Klammer,
 * `JSON.parse` scheitert, und der bisherige Rückfall war: die rohe, escapte
 * JSON-Zeile als Fließtext anzeigen — eine unlesbare Wand (Alex' Screenshot
 * 118490).
 *
 * Statt alles oder nichts werden die Kopf-Felder gezielt herausgelesen. Sie
 * stehen VOR der Abschneidestelle, sind also vollständig. Der Rumpf wird als
 * das gekennzeichnet, was er ist: ein Anfang.
 */
function salvageTruncatedIacpEvent(body: string): IacpEvent | null {
  if (!body.includes('"event"') || !/\[(FULL MESSAGE|MESSAGE TRUNCATED)/i.test(body)) return null;
  const str = (k: string) => new RegExp(`"${k}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`).exec(body)?.[1];
  const num = (k: string) => {
    const m = new RegExp(`"${k}"\\s*:\\s*(\\d+)`).exec(body);
    return m ? Number(m[1]) : undefined;
  };
  const event = str('event');
  const post_id = num('post_id');
  if (!event || post_id === undefined) return null;
  // Der Textanfang bis zur Abschneidestelle — escapte Sequenzen zurückwandeln,
  // sonst stünden dort \n und \" im Fließtext.
  const rawText = str('text') || '';
  const text = rawText.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  return {
    event,
    post_id,
    doc_version: num('doc_version'),
    author_id: str('author_id'),
    trigger: str('trigger'),
    section_id: str('section_id'),
    section_type: str('section_type'),
    section_role: str('section_role'),
    // Erzwingt den Abschneide-Hinweis in der Karte: der Rumpf IST unvollständig.
    text: text ? `${text.trimEnd()} …` : '…',
  } as IacpEvent;
}

export function ToolSection({ section }: { section: V2Section }) {
  const [open, setOpen] = useState(false);
  // IACP zwischen Agenten gibt es auch standalone — nur das WORT „Federation"
  // beschreibt dort eine Struktur, die der Kunde nicht hat (#4701).
  const instanceMode = useInstanceMode();

  // Path 1: backend tagged this with structured IACP metadata.
  if (section.iacp) {
    return (
      <div className="tool-call iacp-tool-call">
        <div className="tool-call-header" onClick={() => setOpen(!open)}>
          <span className={`tool-call-chevron${open ? ' open' : ''}`}><ChevronRight size={12} /></span>
          <IacpToolHeader iacp={section.iacp} />
          {section.iacp.result_id && (
            <span className="iacp-msgid">{section.iacp.result_id.slice(0, 8)}</span>
          )}
        </div>
        <div className={`tool-call-body${open ? '' : ' collapsed'}`}>
          {section.iacp.preview && (
            <div className="iacp-preview">"{section.iacp.preview}"</div>
          )}
          {section.content && (
            <ColoredPre content={section.content} spans={section.spans} />
          )}
        </div>
      </div>
    );
  }

  // Path 2: pre-patch fallback — at least mark cloud-MCP calls visually
  // so users see "↗ Federation Call" rather than the muted "Called cloud…".
  const label = section.label || section.kind;
  if (IACP_FALLBACK_LABEL.test(label)) {
    return (
      <div className="tool-call iacp-tool-call iacp-unknown">
        <div className="tool-call-header" onClick={() => setOpen(!open)}>
          <span className={`tool-call-chevron${open ? ' open' : ''}`}><ChevronRight size={12} /></span>
          <Send size={12} className="iacp-icon" />
          <span className="iacp-label">{instanceMode === 'federation' ? 'Federation' : 'Agenten-Call'}</span>
          <span className="iacp-target iacp-target-unknown">?</span>
        </div>
        <div className={`tool-call-body${open ? '' : ' collapsed'}`}>
          {section.content && <ColoredPre content={section.content} spans={section.spans} />}
        </div>
      </div>
    );
  }

  // Path 3: generic tool call (Bash, Edit, Read, …). Body renders a JSON result
  // as structured PrettyJson (Codex tool-results like cloud.list_agents are big
  // JSON — post #4231, "so geil wie bei Claude"), non-JSON stays monospace, and
  // an oversized/unparseable blob is bounded (never an unbounded raw wall).
  const body = section.content ? stripAnsi(section.content) : '';
  const bodyJson = body ? extractToolResultJson(body) : null;
  return (
    <div className="tool-call">
      <div className="tool-call-header" onClick={() => setOpen(!open)}>
        <span className={`tool-call-chevron${open ? ' open' : ''}`}><ChevronRight size={12} /></span>
        <span className="tool-call-name">{label}</span>
        {section.truncated
          ? <span className="tool-call-status badge badge-warning" title="Das CLI hat die Ausgabe im Terminal abgeschnitten — das JSON ist unvollständig und kann nicht strukturiert dargestellt werden.">gekürzt</span>
          : <span className="tool-call-status badge badge-success">OK</span>}
      </div>
      <div className={`tool-call-body${open ? '' : ' collapsed'}`}>
        {body && (bodyJson ? <PrettyJson data={bodyJson} /> : <RawFallback content={body} />)}
      </div>
    </div>
  );
}

export function DiffSection({ section }: { section: V2Section }) {
  const [open, setOpen] = useState(false);
  const lines = (section.content || '').split('\n');

  return (
    <div className="tool-call">
      <div className="tool-call-header" onClick={() => setOpen(!open)}>
        <span className={`tool-call-chevron${open ? ' open' : ''}`}><ChevronRight size={12} /></span>
        <span className="tool-call-name">{section.label || 'Diff'}</span>
      </div>
      <div className={`tool-call-body${open ? '' : ' collapsed'}`}>
        <div className="diff-block">
          {lines.map((line, i) => {
            let cls = 'context';
            if (line.startsWith('+')) cls = 'add';
            else if (line.startsWith('-')) cls = 'remove';
            else if (line.startsWith('@@')) cls = 'header';
            return (
              <div key={i} className={`diff-line ${cls}`}>
                {line}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function extractMediaUrls(text: string): string[] {
  const urlRegex = /https?:\/\/[^\s)}\]"'`]+/g;
  const matches = (text.match(urlRegex) || []).map((u) => u.replace(/[.,;:!?]+$/, ''));
  return matches.filter((url) =>
    IMAGE_EXTS.test(url) || AUDIO_EXTS.test(url) || STORAGE_MEDIA.test(url)
  );
}

// The agent's reply as Markdown — text/table/tree sections, with
// tool/diff/thinking excluded. Used by the per-reply 🔊 + Copy actions.
export function turnReplyMarkdown(turn: V2Turn): string {
  return (turn.sections || [])
    .map((s) => {
      if (s.kind === 'text') return stripAnsi(s.content || '');
      if (s.kind === 'table') return s.markdown || '';
      if (s.kind === 'tree') return s.markdown_fallback || '';
      return '';
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

// turnNarrationText + its helpers moved to ../lib/narration so the ambient-roam
// controller can narrate a background agent it isn't rendering (#1215).

// Per-reply action row: speak this answer (shared TTS engine) + copy its
// Markdown to the clipboard. Bottom-left, like Claude/ChatGPT (Alex 2026-06-21).
export const TTS_MODES: [TtsMode, string][] = [
  ['native', 'Siri / Browser'],
  ['openai-compact', 'OpenAI Short'],
  ['openai-prose', 'OpenAI Prosa'],
];

// When a reply was finished (Alex 2026-07-30). Date only when it isn't obvious:
// today → time alone, yesterday → "Gestern HH:MM", older → "DD.MM. HH:MM".
// Compares CALENDAR days, not 24h-distances — 23:50 yesterday must read
// "Gestern", not "heute", even though it's only minutes ago.
export function formatTurnTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hhmm = d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (days <= 0) return hhmm;
  if (days === 1) return `Gestern ${hhmm}`;
  const date = d.toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
  });
  return `${date} ${hhmm}`;
}

export function formatTurnDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.round(seconds);
  if (total < 1) return '<1 Sek.';
  if (total < 60) return `${total} Sek.`;
  if (total < 3600) {
    const minutes = Math.floor(total / 60);
    const rest = total % 60;
    return `${minutes} Min.${rest ? ` ${rest} Sek.` : ''}`;
  }
  if (total < 86400) {
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours} Std.${minutes ? ` ${minutes} Min.` : ''}`;
  }
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  return `${days} Tg.${hours ? ` ${hours} Std.` : ''}`;
}

function MessageTime({ label, at }: { label: string; at?: string | null }) {
  const formatted = formatTurnTime(at);
  if (!at || !formatted) return null;
  return (
    <span className="message-time" title={new Date(at).toLocaleString('de-AT')}>
      {label} · {formatted}
    </span>
  );
}

export function ReplyActions({ markdown, at, durationSeconds }: { markdown: string; at?: string | null; durationSeconds?: number | null }) {
  const { speakingId, paused, seekable, loading, loadingSecs, toggle, pauseResume, seek } = useTts();
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState<TtsMode>(getTtsMode);
  const lpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lpFired = useRef(false);
  const hasMarkdown = !!markdown.trim();
  const hasAnswerTime = !!formatTurnTime(at);
  const duration = formatTurnDuration(durationSeconds);
  if (!hasMarkdown && !at && !duration) return null;
  const id = `reply:${markdown.length}:${markdown.slice(0, 40)}`;
  const speaking = speakingId === id;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  const pickMode = (m: TtsMode) => {
    localStorage.setItem('cloud-tts-mode', m);
    setMode(m);
    window.dispatchEvent(new CustomEvent('cloud-tts-mode-change', { detail: m }));
    setMenuOpen(false);
  };
  const startLongPress = () => {
    lpFired.current = false;
    lpTimer.current = setTimeout(() => {
      lpFired.current = true;
      setMenuOpen(true);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 500);
  };
  const cancelLongPress = () => { if (lpTimer.current) clearTimeout(lpTimer.current); };

  return (
    <div className="reply-actions">
      {hasMarkdown && <div className="reply-tts-anchor">
        <button
          type="button"
          className={`reply-action${speaking ? ' active' : ''}`}
          // Long-press (or right-click) opens the TTS-mode menu; a plain
          // tap speaks/stops (Alex 2026-06-21).
          onClick={() => { if (lpFired.current) { lpFired.current = false; return; } toggle(id, markdown); }}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onTouchMove={cancelLongPress}
          onContextMenu={(e) => { e.preventDefault(); setMenuOpen(true); }}
          title={speaking ? (loading ? `Sprachausgabe wird erzeugt… ${loadingSecs}s` : 'Vorlesen stoppen') : 'Vorlesen — lang drücken für Modus'}
        >
          {speaking ? (loading ? <Loader2 size={14} className="reply-spin" /> : <Square size={14} />) : <Volume2 size={14} />}
        </button>
        {/* Elapsed counter while synthesizing — prose runs a GPT summary before
            TTS and legitimately takes 30-50s; the number keeps it from looking
            hung (Alex 2026-07-17). */}
        {speaking && loading && loadingSecs >= 2 && (
          <span className="reply-tts-elapsed">{loadingSecs}s</span>
        )}
        {menuOpen && (
          <>
            <div className="reply-tts-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="reply-tts-menu">
              <div className="reply-tts-head">Vorlese-Stimme</div>
              {TTS_MODES.map(([m, label]) => (
                <button key={m} type="button" className="reply-tts-item" onClick={() => pickMode(m)}>
                  <span className="reply-tts-check">{mode === m ? <Check size={13} /> : null}</span>
                  {label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>}
      {/* ChatGPT-style transport controls while THIS section is playing — only for
          OpenAI TTS (a real, seekable HTMLAudioElement); native Web-Speech can't
          seek so it just gets the Stop above (Alex #206). */}
      {hasMarkdown && speaking && seekable && (
        <>
          <button type="button" className="reply-action reply-tts-seek" onClick={() => seek(-15)} title="15 Sekunden zurück">
            <RotateCcw size={13} /><span className="reply-tts-sec">15</span>
          </button>
          <button type="button" className="reply-action" onClick={pauseResume} title={paused ? 'Weiter' : 'Pause'}>
            {paused ? <Play size={14} /> : <Pause size={14} />}
          </button>
          <button type="button" className="reply-action reply-tts-seek" onClick={() => seek(15)} title="15 Sekunden vor">
            <RotateCw size={13} /><span className="reply-tts-sec">15</span>
          </button>
        </>
      )}
      {hasMarkdown && <button type="button" className="reply-action" onClick={copy} title="Antwort als Markdown kopieren">
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>}
      <MessageTime label="Beantwortet" at={at} />
      {duration && (
        <span className="turn-duration" title={`Turn-Dauer: ${duration}`}>
          {hasAnswerTime ? '· ' : ''}Turn {duration}
        </span>
      )}
    </div>
  );
}

function TurnEntryImpl({ turn, onSelectSession, hideTrailing, anchorKey, userOnly }: { turn: V2Turn; onSelectSession?: (name: string) => void; hideTrailing?: boolean; anchorKey?: string; userOnly?: boolean }) {
  const [collapsed, setCollapsed] = useState(false);
  // Pretty-format toggle for IACP / Queue payloads that turn out to be
  // structured (XML or JSON). The button is only rendered when the
  // sniffer returns a non-text format. Off by default so the chat keeps
  // its tight default look.
  const [pretty, setPretty] = useState(false);
  const { sender, cleanText } = parseTurnMeta(turn);
  // Issue-dispatches (from the issue-api dispatcher) get their OWN look — blue,
  // "Issue"-tag — distinct from regular pink IACP peer messages (Alex 2026-07-04).
  const isIssueDispatch = turn.source === 'iacp'
    && ((sender || '').toLowerCase() === 'issue' || /📋\s*Issue #\d+/.test(cleanText));
  const tag = isIssueDispatch ? 'Issue' : sourceLabel(turn.source);
  const payloadFormat = (turn.source === 'iacp' || turn.source === 'queue')
    ? detectFormat(cleanText)
    : 'text';
  const userMediaUrls = turn.user ? extractMediaUrls(turn.user) : [];
  // Medien-URLs, die unten als Thumbnail erscheinen, NICHT zusätzlich als
  // Text-Link im Eingabe-Absatz zeigen (Alex 17.08., Screenshot 119747:
  // „den Link musst du nicht nochmal anzeigen, wenn eh das Bild gezeigt
  // wird"). Nur für direkte User-Eingaben — IACP-/Queue-Payloads bleiben
  // wörtlich, dort ist die URL Teil des Vertrags-Inhalts.
  const textSansMedia = (turn.source === 'direct' && userMediaUrls.length > 0)
    ? userMediaUrls.reduce((acc, u) => acc.split(u).join(''), cleanText).replace(/[ \t]{2,}/g, ' ').trim()
    : cleanText;
  const displayText = (pretty && payloadFormat !== 'text')
    ? prettyFormat(cleanText, payloadFormat)
    : textSansMedia;

  const handleDoubleClick = () => {
    if ((turn.source === 'iacp' || turn.source === 'queue') && sender && onSelectSession) {
      onSelectSession(sender);
    }
  };

  // Structured IACP event (Cloud spec dbac89be): render as card instead
  // of plain user text. Falls through to standard rendering when absent.
  //
  // `turn.event ?? parseEmbeddedIacpEvent(turn.user)`: the backend does not
  // always tag an incoming push as an event — then the raw JSON payload landed
  // in the conversation as a wall of `{"event":"chunk_committed",…}` including
  // escaped `\n\n<main>` markup (Alex 2026-08-03, screenshot 117236: "manchmal
  // habe ich es so, manchmal so"). The difference was never the renderer, it
  // was whether `turn.event` was set. Sniffing the payload client-side closes
  // that gap without waiting on the parser.
  // Arcturian-Zustellung: der servergesetzte Marker wird als Protokoll
  // ausgewertet statt als Fliesstext gerendert (Alex 2026-08-06). VOR der
  // IACP-Event-Karte geprüft, weil eine Arcturian-Nachricht formal auch eine
  // IACP-Nachricht ist — die speziellere Darstellung gewinnt. Fällt der Parser
  // aus (fail-closed), greift automatisch wieder der bisherige Weg.
  const arcturian = parseArcturianEnvelope(turn.user);
  if (arcturian) {
    return (
      <div className="stream-entry" data-turnkey={anchorKey}>
        <ArcturianMessage env={arcturian} />
        <MessageTime label="Gesendet" at={turn.timestamp} />
        {!userOnly && !collapsed && turn.sections.length > 0 && (
          <div className="entry-agent">
            {turn.sections.map((sec, i) => {
              if (sec.kind === 'tool') return <ToolSection key={i} section={sec} />;
              if (sec.kind === 'diff') return <DiffSection key={i} section={sec} />;
              if (sec.kind === 'thinking') return null;
              return <SectionContent key={i} section={sec} />;
            })}
          </div>
        )}
      </div>
    );
  }

  const iacpEvent = turn.event ?? parseEmbeddedIacpEvent(turn.user);
  if (iacpEvent) {
    return (
      <div className="stream-entry" data-turnkey={anchorKey}>
        <IacpEventCard event={iacpEvent} sender={turn.sender || sender || undefined} />
        <MessageTime label="Gesendet" at={turn.timestamp} />
        {!userOnly && !collapsed && turn.sections.length > 0 && (
          <div className="entry-agent">
            {turn.sections.map((sec, i) => {
              if (sec.kind === 'tool') return <ToolSection key={i} section={sec} />;
              if (sec.kind === 'diff') return <DiffSection key={i} section={sec} />;
              if (sec.kind === 'thinking') return null;
              return <SectionContent key={i} section={sec} />;
            })}
            {!hideTrailing && (
              <ReplyActions
                markdown={turnReplyMarkdown(turn)}
                at={turn.ended_at}
                durationSeconds={turn.duration_s}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    // `data-own` markiert die eigenen Nachrichten fuer den Sprung mit
    // Strg/Alt+Pfeil (Alex 2026-08-12). Die Regel dafuer ist NICHT neu:
    // `parseTurnMeta` liefert `sender === null` genau dann, wenn im Zug weder
    // ein [IACP:…]- noch ein [Queue …]-Umschlag steckt. Eine zweite Kopie
    // dieser Unterscheidung waere genau die Drift, die heute anderswo teuer war.
    <div className="stream-entry" data-turnkey={anchorKey} data-own={!sender && turn.user ? '1' : undefined}>
      {turn.user && (
        <div className={`entry-user source-${turn.source}${isIssueDispatch ? ' source-issue' : ''}`}>
          <div className="entry-marker" />
          <div>
            <div
              className={`entry-label${sender && (turn.source === 'iacp' || turn.source === 'queue') ? ' entry-label-clickable' : ''}`}
              onDoubleClick={handleDoubleClick}
            >
              {turn.source === 'iacp'
                ? sender || 'Agent'
                : turn.source === 'queue'
                  ? sender || 'Queue'
                  : turn.source === 'system'
                    ? 'System'
                    : turn.submitted_by
                      ? <SubmitterName id={turn.submitted_by} />
                      : 'User Input'}
              {tag && (
                <span className={`entry-source-tag ${turn.source}`}>{tag}</span>
              )}
              {payloadFormat !== 'text' && (
                <button
                  type="button"
                  className={`format-toggle${pretty ? ' active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setPretty((p) => !p); }}
                  title={pretty ? 'Roh anzeigen' : `Als ${payloadFormat.toUpperCase()} formatieren`}
                >
                  {payloadFormat === 'json' ? '{ }' : '< >'}
                </button>
              )}
            </div>
            <div
              className={`entry-content${pretty && payloadFormat !== 'text' ? ' is-pretty' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setCollapsed(!collapsed)}
            >
              {pretty && payloadFormat !== 'text' ? (
                <pre className="payload-pretty">{displayText}</pre>
              ) : (() => {
                // Auto-pretty embedded JSON blocks within queue/iacp
                // payloads — the format-toggle above only handles ONE
                // format at a time (XML wins when XML comes first), so
                // a mixed payload like "<gps_update>… {persona-reply}"
                // would leave the JSON portion as raw curly soup. We
                // split on JSON boundaries and render those via
                // PrettyJson always; the surrounding text/XML stays as
                // plain text (or gets toggled to pretty-XML above).
                // Alex's GPS-Tracker → GuideTest case (screenshot 110660).
                if (turn.source === 'queue' || turn.source === 'iacp') {
                  const parts = splitTextAndJson(displayText);
                  if (parts.length > 1 || (parts[0] && parts[0].type === 'json')) {
                    return parts.map((p, i) =>
                      p.type === 'json' && p.data ? (
                        hasSemanticFields(p.data)
                          ? <SemanticAgentCard key={i} data={p.data as Record<string, unknown>} />
                          : <PrettyJson key={i} data={p.data} />
                      ) : (
                        <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{p.content}</span>
                      )
                    );
                  }
                }
                return displayText;
              })()}
            </div>
            {userMediaUrls.length > 0 && (
              <div className="user-media-row">
                {userMediaUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={STORAGE_MEDIA.test(url) ? storageMediaUrl(url) : url}
                      alt=""
                      className="user-media-thumb"
                    />
                  </a>
                ))}
              </div>
            )}
            <MessageTime label="Gesendet" at={turn.timestamp} />
          </div>
        </div>
      )}

      {!userOnly && !collapsed && turn.sections.length > 0 && (
        <div className="entry-agent">
          {turn.sections.map((sec, i) => {
            if (sec.kind === 'tool') return <ToolSection key={i} section={sec} />;
            if (sec.kind === 'diff') return <DiffSection key={i} section={sec} />;
            if (sec.kind === 'thinking') return null;
            // While the agent is still working, suppress the post-turn recap
            // + task-list on the active turn — they're only meaningful once
            // the turn is finished (Alex 2026-06-22).
            if (hideTrailing && (sec.kind === 'recap' || sec.kind === 'tasklist')) return null;
            return <SectionContent key={i} section={sec} />;
          })}
          {/* Reply actions (Vorlesen / Kopieren) appear only when the turn is
              done — hidden while the agent is actively producing it. */}
          {!hideTrailing && (
            <ReplyActions
              markdown={turnReplyMarkdown(turn)}
              at={turn.ended_at}
              durationSeconds={turn.duration_s}
            />
          )}
        </div>
      )}
    </div>
  );
}

// TurnEntry is memoized so a streaming agent doesn't re-render the ENTIRE
// conversation on every chunk. Root cause of Alex's 3-10s typing freeze
// (2026-07-01): useSessionV2 re-fetches the whole /v2 on each session.pane_dirty,
// so every turn arrives as a fresh object ref; with an unmemoized TurnEntry the
// full Markdown/Mermaid of ALL turns re-rendered per chunk, saturating the main
// thread. During streaming only the LAST turn's content actually changes — all
// prior turns are byte-identical (just new refs). A content-signature compare
// lets React skip the unchanged ones, so a chunk re-renders 1 turn, not N.
const _turnSig = new WeakMap<object, string>();
function turnSig(t: V2Turn): string {
  let s = _turnSig.get(t);
  if (s === undefined) { s = JSON.stringify(t); _turnSig.set(t, s); }
  return s;
}
export const TurnEntry = memo(TurnEntryImpl, (a, b) =>
  a.hideTrailing === b.hideTrailing &&
  // Ohne diese Zeile bliebe die Antwort beim Filterwechsel stehen: Der
  // Vergleich saehe denselben Zug und uebersaehe, dass sich die SICHT
  // geaendert hat.
  a.userOnly === b.userOnly &&
  a.anchorKey === b.anchorKey &&
  // Compare handler presence, not identity: a navigation callback's identity
  // may churn without changing what this turn renders, and keying memo on it
  // would defeat the whole optimization.
  !!a.onSelectSession === !!b.onSelectSession &&
  turnSig(a.turn) === turnSig(b.turn)
);
